import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useProductActivation, useProductDetail } from "~/features/admin/products/hooks";
import { toProductDetailUI } from "~/features/admin/products/utils/transformers";
import { ProductDetailPresenter } from "./ProductDetailPresenter";
import { ProductFormDialog } from "./ProductFormDialog";

interface ProductDetailProps {
  productId: string;
  /** Back action: close the sheet (peek) or navigate to the list (full page). */
  onBack: () => void;
}

/**
 * Reusable from both the full-page route and the Peek side panel. Owns data,
 * the edit dialog, and the activation confirm flow; renders the presenter.
 */
export function ProductDetail({ productId, onBack }: ProductDetailProps) {
  const { t } = useTranslation();
  const { data } = useProductDetail(productId);
  const { onActivate, onDeactivate, processing } = useProductActivation();
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const product = data.productsByPk;
  const detail = useMemo(() => (product ? toProductDetailUI(product) : null), [product]);

  if (!detail) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        {t("ui.notFound")}
      </div>
    );
  }

  const handleConfirm = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: detail.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: detail.id });
    }
    setPendingAction(null);
  };

  return (
    <>
      <ProductDetailPresenter
        product={detail}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        pendingAction={pendingAction}
        confirmLabel={
          pendingAction === "activate" ? t("ui.activateX", { x: detail.name }) : t("ui.deactivateX", { x: detail.name })
        }
        processing={processing}
        onConfirm={handleConfirm}
        onCancelConfirm={() => setPendingAction(null)}
      />
      <ProductFormDialog open={editOpen} onOpenChange={setEditOpen} productId={detail.id} />
    </>
  );
}
